import React, { useState, useEffect } from 'react';
import { 
  Employee, 
  ShiftType, 
  EmployeeAvailabilityResponse,
  CalendarFormData, 
  CalendarDay, 
  AvailabilitySelection,
  CalendarPeriod,
  SetDayAvailabilityRequest,
  BulkAction
} from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

interface AvailabilityFormProps {
  employee: Employee;
}

const AvailabilityForm: React.FC<AvailabilityFormProps> = ({ employee }) => {
  // Hardcoded shift types instead of fetching from API
  const shiftTypes: ShiftType[] = [
    { id: 1, name: 'morning', description: 'Morning Shift', displayOrder: 1, isActive: true, createdAt: new Date().toISOString() },
    { id: 2, name: 'evening', description: 'Evening Shift', displayOrder: 2, isActive: true, createdAt: new Date().toISOString() }
  ];
  
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [formData, setFormData] = useState<CalendarFormData>({});
  const [currentPeriod, setCurrentPeriod] = useState<CalendarPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Bulk action definitions
  const bulkActions: BulkAction[] = [
    {
      type: 'all-available',
      label: 'Available All Days',
      description: 'Set available for both shifts on all days'
    },
    {
      type: 'weekdays-only',
      label: 'Weekdays Only',
      description: 'Available both shifts Mon-Fri, not available weekends'
    },
    {
      type: 'weekends-only',
      label: 'Weekends Only',
      description: 'Available both shifts Sat-Sun, not available weekdays'
    },
    {
      type: 'all-unavailable',
      label: 'Not Available',
      description: 'Set unavailable for all days'
    }
  ];

  useEffect(() => {
    loadData();
  }, [employee.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get default date range (14-day period starting from next Monday)
      const dateRange = await apiService.getAvailabilityDateRange();
      
      // Load current availability (shift types are now hardcoded)
      const availabilityResponse = await apiService.getEmployeeAvailabilityForDateRange(
        employee.id, 
        dateRange.startDate, 
        dateRange.endDate
      );

      if (!availabilityResponse.success) {
        throw new Error(availabilityResponse.message || 'Failed to load current availability');
      }

      const availability = availabilityResponse.data || { 
        employeeId: employee.id, 
        periodStart: dateRange.startDate,
        periodEnd: dateRange.endDate,
        availability: [] 
      };

      // Create calendar period info
      const period: CalendarPeriod = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        displayName: apiService.formatDateRange(dateRange.startDate, dateRange.endDate)
      };
      setCurrentPeriod(period);

      // Generate calendar days
      const days = generateCalendarDays(dateRange.startDate, dateRange.endDate, availability);
      setCalendarDays(days);

      // Initialize form data
      const initialFormData: CalendarFormData = {};
      days.forEach(day => {
        initialFormData[day.date] = {
          selection: day.availability,
          notes: ''
        };
      });
      setFormData(initialFormData);

    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarDays = (startDate: string, endDate: string, availability: EmployeeAvailabilityResponse): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const current = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    while (current <= end) {
      const dateStr = apiService.formatDateForAPI(current);
      const dayAvailability = availability.availability.find(a => a.date === dateStr);
      
      // Determine availability selection based on shift types available
      let selection: AvailabilitySelection = 'none';
      if (dayAvailability && dayAvailability.shiftTypes.length > 0) {
        const shiftTypes = dayAvailability.shiftTypes;
        if (shiftTypes.length === 2) {
          selection = 'both';
        } else if (shiftTypes.includes('morning')) {
          selection = 'morning';
        } else if (shiftTypes.includes('evening')) {
          selection = 'evening';
        }
      }

      days.push({
        date: dateStr,
        dayOfWeek: apiService.getDayOfWeek(dateStr),
        isInCurrentMonth: true, // All days in our 14-day period are relevant
        isPastDate: apiService.isPastDate(dateStr),
        isTooFarFuture: apiService.isTooFarFuture(dateStr),
        availability: selection
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const handleDayAvailabilityChange = async (date: string, selection: AvailabilitySelection) => {
    // Update local state immediately for responsive UI
    setFormData(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        selection
      }
    }));

    // Update calendar days for immediate visual feedback
    setCalendarDays(prev => prev.map(day => 
      day.date === date ? { ...day, availability: selection } : day
    ));

    // Save to API immediately
    try {
      setError(null);
      
      // Convert selection to shift type IDs
      const shiftTypeIds: number[] = [];
      
      if (selection === 'both') {
        shiftTypeIds.push(...shiftTypes.map(st => st.id));
      } else if (selection === 'morning') {
        const morningType = shiftTypes.find(st => st.name === 'morning');
        if (morningType) shiftTypeIds.push(morningType.id);
      } else if (selection === 'evening') {
        const eveningType = shiftTypes.find(st => st.name === 'evening');
        if (eveningType) shiftTypeIds.push(eveningType.id);
      }
      // 'none' = empty array

      const dayRequest: SetDayAvailabilityRequest = {
        shiftTypeIds,
        notes: formData[date]?.notes?.trim() || undefined
      };

      const response = await apiService.setDayAvailability(employee.id, date, dayRequest);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to save availability');
      }

      // Show brief success feedback
      setSuccessMessage(`Availability saved for ${apiService.formatDisplayDate(date)}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

    } catch (err) {
      console.error('Failed to save day availability:', err);
      setError(err instanceof Error ? err.message : 'Failed to save availability');
      
      // Revert the UI change on error
      setFormData(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          selection: prev[date]?.selection || 'none' // Revert to previous value
        }
      }));
      
      setCalendarDays(prev => prev.map(day => 
        day.date === date ? { ...day, availability: prev.find(d => d.date === date)?.availability || 'none' } : day
      ));
    }
  };

  const handleBulkAction = async (action: BulkAction) => {
    try {
      setError(null);
      setSuccessMessage(null);

      // Determine the selection based on action type
      let selection: AvailabilitySelection = 'none';
      
      switch (action.type) {
        case 'all-available':
          selection = 'both';
          break;
        case 'all-unavailable':
          selection = 'none';
          break;
        case 'weekdays-only':
        case 'weekends-only':
          selection = 'both'; // We'll filter by weekdays/weekends in the loop
          break;
        default:
          selection = 'none';
      }

      // Apply the bulk action to each applicable day
      const updates: Promise<void>[] = [];
      
      for (const day of calendarDays) {
        if (day.isPastDate) continue; // Skip past dates
        
        let daySelection = selection;
        
        // Special handling for weekday-only and weekend-only actions
        if (action.type === 'weekdays-only') {
          const isWeekend = apiService.isWeekend(day.date);
          daySelection = isWeekend ? 'none' : 'both';
        } else if (action.type === 'weekends-only') {
          const isWeekend = apiService.isWeekend(day.date);
          daySelection = isWeekend ? 'both' : 'none';
        }
        
        // Skip if the day is already set to this selection
        if (day.availability === daySelection) continue;
        
        // Update local state immediately
        setFormData(prev => ({
          ...prev,
          [day.date]: {
            ...prev[day.date],
            selection: daySelection
          }
        }));
        
        setCalendarDays(prev => prev.map(d => 
          d.date === day.date ? { ...d, availability: daySelection } : d
        ));
        
        // Queue the API update
        const shiftTypeIds: number[] = [];
        
        if (daySelection === 'both') {
          shiftTypeIds.push(...shiftTypes.map(st => st.id));
        }
        // For 'none', we leave shiftTypeIds empty
        
        const dayRequest: SetDayAvailabilityRequest = {
          shiftTypeIds,
          notes: formData[day.date]?.notes?.trim() || undefined
        };
        
        updates.push(
          apiService.setDayAvailability(employee.id, day.date, dayRequest)
            .then(response => {
              if (!response.success) {
                throw new Error(response.message || `Failed to save availability for ${day.date}`);
              }
            })
        );
      }
      
      // Execute all updates
      await Promise.all(updates);
      
      setSuccessMessage(`Bulk action "${action.label}" applied successfully`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (err) {
      console.error('Failed to apply bulk action:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply bulk action');
      
      // Reload data to restore correct state
      await loadData();
    }
  };

  const getAvailabilityLabel = (selection: AvailabilitySelection): string => {
    switch (selection) {
      case 'both': return 'Both';
      case 'morning': return 'Morning';
      case 'evening': return 'Evening';
      case 'none': return 'None';
    }
  };

  const getAvailabilityColor = (selection: AvailabilitySelection): string => {
    switch (selection) {
      case 'both': return 'bg-green-100 text-green-800 border-green-200';
      case 'morning': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'evening': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'none': return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner message="Loading availability calendar..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage 
        title="Failed to Load Data"
        message={error}
        showRetry={true}
        onRetry={loadData}
      />
    );
  }

  if (!currentPeriod) {
    return <ErrorMessage title="Error" message="Failed to load calendar period" />;
  }

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Help section */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">How the Calendar Works</h4>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Both shifts:</strong> Available for morning and evening shifts that day</li>
                <li><strong>Morning only:</strong> Available only for morning shifts</li>
                <li><strong>Evening only:</strong> Available only for evening shifts</li>
                <li><strong>Not available:</strong> Cannot work any shifts that day</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {bulkActions.map(action => (
            <button
              key={action.type}
              onClick={() => handleBulkAction(action)}
              className="p-3 text-left border border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              title={action.description}
            >
              <div className="text-sm font-medium text-gray-900 mb-1">
                {action.label}
              </div>
              <div className="text-xs text-gray-600">
                {action.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {/* Calendar Header */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Availability for {currentPeriod.displayName}
            </h3>
            <div className="text-sm text-gray-600">
              14-day schedule period
            </div>
          </div>

          {/* Calendar Grid */}
          {/* Desktop view - 7 columns */}
          <div className="hidden md:grid md:grid-cols-7 gap-2">
            {/* Day headers */}
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-700 py-2">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {calendarDays.map(day => {
              const dayData = formData[day.date];
              const isWeekend = apiService.isWeekend(day.date);
              
              return (
                <div
                  key={day.date}
                  className={`border rounded-lg p-2 min-h-[100px] ${
                    day.isPastDate 
                      ? 'bg-gray-50 border-gray-200' 
                      : 'bg-white border-gray-300 hover:border-primary-300'
                  } ${isWeekend ? 'bg-blue-50' : ''}`}
                >
                  <div className="text-xs font-medium text-gray-900 mb-1">
                    {new Date(day.date + 'T00:00:00').getDate()}
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    {apiService.formatDisplayDate(day.date).split(' ')[0].replace(',','')} {/* Day of week */}
                  </div>
                  
                  {!day.isPastDate && (
                    <div className="space-y-1">
                      <select
                        value={dayData?.selection || 'none'}
                        onChange={(e) => handleDayAvailabilityChange(day.date, e.target.value as AvailabilitySelection)}
                        className="w-full text-xs border border-gray-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        disabled={day.isPastDate}
                      >
                        <option value="none">Not available</option>
                        <option value="morning">Morning only</option>
                        <option value="evening">Evening only</option>
                        <option value="both">Both shifts</option>
                      </select>
                      
                      <div className={`text-xs px-1 py-0.5 rounded text-center border ${getAvailabilityColor(day.availability)}`}>
                        {getAvailabilityLabel(day.availability)}
                      </div>
                    </div>
                  )}
                  
                  {day.isPastDate && (
                    <div className="text-xs text-gray-500 text-center">
                      Past date
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile view - List format */}
          <div className="md:hidden space-y-3">
            {calendarDays.map(day => {
              const dayData = formData[day.date];
              const isWeekend = apiService.isWeekend(day.date);
              const dayOfWeek = apiService.formatDisplayDate(day.date).split(' ')[0];
              const dayNum = new Date(day.date + 'T00:00:00').getDate();
              const monthDay = apiService.formatDisplayDate(day.date);
              
              return (
                <div
                  key={day.date}
                  className={`border rounded-lg p-3 ${
                    day.isPastDate 
                      ? 'bg-gray-50 border-gray-200' 
                      : 'bg-white border-gray-300'
                  } ${isWeekend ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl font-bold text-gray-900">
                        {dayNum}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {dayOfWeek}
                        </div>
                        <div className="text-xs text-gray-600">
                          {monthDay.replace(dayOfWeek + ' ', '')}
                        </div>
                      </div>
                      {isWeekend && (
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          Weekend
                        </div>
                      )}
                    </div>
                    
                    {!day.isPastDate && (
                      <div className={`text-xs px-2 py-1 rounded-full border ${getAvailabilityColor(day.availability)}`}>
                        {getAvailabilityLabel(day.availability)}
                      </div>
                    )}
                  </div>
                  
                  {!day.isPastDate ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Availability
                      </label>
                      <select
                        value={dayData?.selection || 'none'}
                        onChange={(e) => handleDayAvailabilityChange(day.date, e.target.value as AvailabilitySelection)}
                        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        disabled={day.isPastDate}
                      >
                        <option value="none">Not available</option>
                        <option value="morning">Morning shift only</option>
                        <option value="evening">Evening shift only</option>
                        <option value="both">Both shifts</option>
                      </select>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Past date - no changes allowed
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityForm;
